import { motion, AnimatePresence } from "framer-motion";
import { HiX, HiChevronLeft, HiChevronRight } from "react-icons/hi";
import { FaCheck } from "react-icons/fa";

export const ProductLightbox = ({ product, onClose, onPrev, onNext, hasPrev, hasNext }) => {
  if (!product) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
        onClick={onClose}
        data-testid="product-lightbox"
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-obsidian/95 backdrop-blur-xl" />

        {/* Content */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", damping: 25 }}
          className="relative z-10 w-full max-w-5xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            data-testid="lightbox-close"
            aria-label="Close"
          >
            <HiX className="text-white text-2xl" />
          </button>

          <div className="flex flex-col md:flex-row bg-charcoal rounded-2xl overflow-hidden border border-white/10">
            {/* Image */}
            <div className="relative md:w-1/2">
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full h-64 md:h-full object-cover"
              />
              {/* Navigation Arrows */}
              {hasPrev && (
                <button
                  onClick={onPrev}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-obsidian/80 hover:bg-gold-500/20 transition-colors border border-white/10"
                  data-testid="lightbox-prev"
                  aria-label="Previous"
                >
                  <HiChevronLeft className="text-white text-2xl" />
                </button>
              )}
              {hasNext && (
                <button
                  onClick={onNext}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-obsidian/80 hover:bg-gold-500/20 transition-colors border border-white/10"
                  data-testid="lightbox-next"
                  aria-label="Next"
                >
                  <HiChevronRight className="text-white text-2xl" />
                </button>
              )}
            </div>

            {/* Details */}
            <div className="md:w-1/2 p-8 md:p-10 overflow-y-auto max-h-[60vh] md:max-h-[80vh]">
              <span className="inline-block px-3 py-1 text-xs font-medium text-gold-400 bg-gold-500/10 rounded-full capitalize">
                {product.category} Series
              </span>
              <h2 className="font-heading text-3xl md:text-4xl text-white mt-4">
                {product.name}
              </h2>
              <p className="text-gray-400 mt-4 leading-relaxed">
                {product.description}
              </p>

              {/* Features */}
              {product.features && product.features.length > 0 && (
                <div className="mt-8">
                  <h3 className="font-heading text-lg text-white mb-4">Features</h3>
                  <ul className="space-y-3">
                    {product.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-3 text-gray-300">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gold-500/20 flex items-center justify-center">
                          <FaCheck className="text-gold-500 text-xs" />
                        </span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* CTA */}
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center px-8 py-3 bg-gradient-to-r from-gold-600 to-gold-500 text-obsidian font-semibold rounded-full hover:scale-105 transition-transform"
                  data-testid="lightbox-inquire"
                >
                  Inquire Now
                </a>
                <a
                  href={`tel:+18482198830`}
                  className="inline-flex items-center justify-center px-8 py-3 border border-gold-500/30 text-gold-400 font-medium rounded-full hover:bg-gold-500/10 transition-colors"
                >
                  Call Us
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
